import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { useFetchAllProjectAssetsQuery } from './useFetchAllProjectAssetsQuery';
import { useFetchAssetsAndFoldersQuery } from './useFetchAssetsAndFoldersQuery';
import { useFilterContext } from '../contexts/FilterContext';
import { checkArrayLength } from 'modules/campaignIndex/utils';
import { useFeatureFlag } from 'utils/hooks/useFeatureFlag';

export function useAssetsFetching({
  isPanel,
  append,
  sortBy,
  sortDirection,
  currentPage,
  pageSize,
  folderId,
}: {
  isPanel: boolean;
  append?: boolean;
  sortBy: string;
  sortDirection: SORT_DIRECTIONS;
  currentPage: number;
  pageSize: number;
  folderId: number;
}) {
  const isCreativeLibraryAssetsFolderPanelFFEnabled = useFeatureFlag(
    'CreativeLibraryAssetsFolderPanel',
    false,
  );
  const { fileType, updatedByUserId, createdByUserId, searchQuery, fileSize } = useFilterContext();
  // Fetch assets for panel view
  // NOTE: this is a temporary solution to avoid breaking changes in the panel
  const {
    results: panelAssets = [],
    info: panelInfo,
    isLoading: panelAssetsLoading,
    hasError: panelAssetsHasError,
    refetch: panelAssetsRefetch,
  } = useFetchAllProjectAssetsQuery(
    {
      append,
      search: searchQuery,
      sortBy,
      sortDirection,
      offset: (currentPage - 1) * pageSize,
      count: pageSize,
    },
    { skip: !isPanel || !!isCreativeLibraryAssetsFolderPanelFFEnabled },
  );

  // Fetch assets and folders for the creative library index view
  const {
    childFolders,
    childAssets,
    folderData,
    info: folderInfo,
    isLoading,
    hasError,
    refetch,
  } = useFetchAssetsAndFoldersQuery(
    {
      folderId,
      pagination: {
        offset: (currentPage - 1) * pageSize,
        limit: pageSize,
      },
      search: searchQuery,
      assetFilterInfo: {
        createdByUserId: checkArrayLength(createdByUserId),
        updatedByUserId: checkArrayLength(updatedByUserId),
        mimeType: checkArrayLength(fileType),
        size: checkArrayLength(fileSize),
      },
      sort: {
        sortBy,
        sortDirection,
      },
    },
    { skip: isPanel && !isCreativeLibraryAssetsFolderPanelFFEnabled },
  );

  if (isPanel && !isCreativeLibraryAssetsFolderPanelFFEnabled) {
    return {
      assets: panelAssets,
      folders: [],
      assetCount: panelAssets.length,
      assetCountTotal: panelInfo?.count || 0,
      assetPage: panelInfo?.page || 1,
      currentFolder: null,
      isLoading: panelAssetsLoading,
      hasError: panelAssetsHasError,
      refetch: panelAssetsRefetch,
    };
  } else {
    return {
      assets: childAssets,
      folders: childFolders,
      assetCount: childAssets.length + childFolders.length,
      assetCountTotal: folderInfo?.count || 0,
      assetPage: folderInfo?.page || 1,
      currentFolder: folderData,
      isLoading,
      hasError,
      refetch,
    };
  }
}
